/**
 * The day's fuller breakdown: fibre, sugar, salt and saturated fat.
 *
 * **No progress bar, and no colour.** `MacroBar` is a `role="progressbar"`
 * whose `aria-valuemax` is the target — and there is no target for these. With
 * none, ARIA defaults the maximum to 100, so "salt 1.8" announces as *"1.8,
 * one hundred percent"*; and a track drawn with no fill reads visually as
 * zero. Both are worse than saying nothing. Colour is ruled out by
 * `tokens.css`: the hue tokens are named by job, and nothing that is not a
 * ring arc, a bar, a chart mark or the primary action may reference one.
 * Staying in `--ink-62` also does the placement work, subordinating
 * this to the three bars above it, which is what "leave the ring alone" means
 * in practice.
 *
 * **Coverage is stated, not implied.** Most foods carry these figures and some
 * do not — a quick add never does — so a bare total would claim a completeness
 * the day has not got. The rules, in order of how often they fire:
 *
 * - every entry could say → the figure alone. "from 9 of 9" on every row is
 *   noise, and once it is noise nobody reads the one row that says 6.
 * - some could → the figure, and the count underneath.
 * - none could → an em dash. Never `0 g`: a blank is a question and a zero is
 *   an assertion, and asserting that a day of home cooking had no sugar in it
 *   is the exact failure this whole feature exists to avoid.
 */

import {
  formatNutrient,
  isComplete,
  NUTRIENTS,
  type NutrientTotals,
  worstCoverage,
} from "@calorio/core";

const DETAIL_ID = "today-detail";

export function NutrientDetail({
  totals,
  open,
  onToggle,
}: {
  totals: NutrientTotals;
  open: boolean;
  onToggle: () => void;
}) {
  const complete = isComplete(totals);
  const worst = worstCoverage(totals);

  return (
    <div className="today__more">
      <button
        type="button"
        className="today__more-head"
        aria-expanded={open}
        aria-controls={DETAIL_ID}
        onClick={onToggle}
      >
        <span className="today__more-label">More</span>
        {/* A disclosure that says nothing about what is behind it is a mystery
            button. When something is missing, saying so is the one fact needed
            to decide whether to open it — and it is the worst row, not an
            average, because an average across four nutrients describes no
            entry. */}
        {!complete && worst && (
          <span className="num today__more-summary">
            {worst.counted} of {worst.of} entries
          </span>
        )}
      </button>

      {open && (
        <dl className="detail" id={DETAIL_ID}>
          {NUTRIENTS.map((spec) => {
            const row = totals[spec.key];
            const partial = row.counted > 0 && row.counted < row.of;
            const none = row.counted === 0;
            const fromId = `${spec.key}-from`;
            return (
              <div className="detail__row" key={spec.key}>
                <dt className="detail__name">{spec.label}</dt>
                {/* The count has to reach the same announcement as the figure.
                    A bare sibling paragraph is read two elements later, by
                    which point "48" has already been taken at face value. */}
                <dd
                  className="detail__value num"
                  aria-describedby={partial || none ? fromId : undefined}
                >
                  {none ? (
                    "—"
                  ) : (
                    <>
                      {formatNutrient(spec, row.total)}
                      <span className="detail__unit"> {spec.unit}</span>
                    </>
                  )}
                </dd>
                {(partial || none) && (
                  <p className="detail__from" id={fromId}>
                    {none
                      ? `no entry has a ${spec.label.toLowerCase()} figure`
                      : `from ${row.counted} of ${row.of} ${row.of === 1 ? "entry" : "entries"}`}
                  </p>
                )}
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

/**
 * The same four on one open meal, as a single line.
 *
 * One line rather than the day's four-row list, because the question is
 * different: the day's figure is the one you act on, while the meal's answers
 * "*which* meal was the sugar" — a comparison across four meals, which one
 * line each supports and four stacked lists do not. It also costs about 18px
 * per open meal against ninety, and meals collapse in the first place because
 * the day did not fit a phone.
 *
 * Not on the *collapsed* meal head: that row already carries a name, a count
 * and a kcal figure, and a fourth item wraps at 402px.
 */
export function MealNutrients({ totals }: { totals: NutrientTotals }) {
  const worst = worstCoverage(totals);
  if (!worst || worst.of === 0) return null;
  const complete = isComplete(totals);

  return (
    <p className="meal__detail num">
      {NUTRIENTS.map((spec, index) => {
        const row = totals[spec.key];
        return (
          <span key={spec.key}>
            {index > 0 && " · "}
            {spec.shortLabel} {row.counted === 0 ? "—" : formatNutrient(spec, row.total)}
          </span>
        );
      })}
      {!complete && (
        <span>
          {" · "}
          {worst.counted} of {worst.of}
        </span>
      )}
    </p>
  );
}
